// src/components/LoadErrorBanner.jsx
// Most pages swallow fetch errors (.catch(() => {})), so a failed request used
// to render as an empty page — indistinguishable from genuinely having no data,
// with no way to retry. The api interceptor now emits `tt-load-failed` when a
// GET fails with nothing cached to fall back on; this surfaces that once, with
// a retry, instead of leaving the student guessing.
import { useState, useEffect, useRef } from 'react';

export default function LoadErrorBanner() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const onFail = () => {
      // Offline has its own banner; don't stack two messages saying the same thing.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      setShow(true);
      // Auto-dismiss: a later request often succeeds (cold backend warming up),
      // and a stuck banner would be its own bug.
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setShow(false), 12000);
    };
    const onOk = () => setShow(false);
    window.addEventListener('tt-load-failed', onFail);
    window.addEventListener('online', onOk);
    return () => {
      window.removeEventListener('tt-load-failed', onFail);
      window.removeEventListener('online', onOk);
      clearTimeout(timer.current);
    };
  }, []);

  if (!show) return null;

  return (
    <div role="status" style={{
      position: 'fixed', left: '50%', transform: 'translateX(-50%)',
      bottom: 'max(84px, calc(env(safe-area-inset-bottom) + 84px))',
      zIndex: 400, maxWidth: 'calc(100vw - 32px)',
      display: 'flex', alignItems: 'center', gap: 12,
      background: 'var(--txt)', color: '#fff',
      borderRadius: 12, padding: '11px 14px',
      boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
      fontSize: 13, fontWeight: 600,
    }}>
      <span>Couldn't load — check your connection.</span>
      <button
        onClick={() => { setBusy(true); window.location.reload(); }}
        disabled={busy}
        style={{
          background: 'var(--org)', color: '#fff', border: 'none',
          borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
          fontSize: 12.5, fontWeight: 800, flexShrink: 0,
        }}>
        {busy ? '…' : 'Retry'}
      </button>
      <button onClick={() => setShow(false)} aria-label="Dismiss"
        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>
        ×
      </button>
    </div>
  );
}
